import { Injectable } from "@angular/core";
import { AuthService } from "src/app/core/services/auth.service";
import { TokenAreaPermissao, TokenLogin } from "../models/login-response";
import { Permission } from "../models/permission";

/**
 * Serviço para criar/consultar/limpar dados de permisão do usuário
 */
@Injectable({
  providedIn: "root",
})
export class PermissionService {
  private permissionAreas: TokenAreaPermissao[] = [];
  private permissionTable: Permission[] = [];
  private isEditorUser: boolean = false;

  constructor(private authService: AuthService) {
    // Cria as permissões no constructor para o caso de atualizar a página
    this.createPermissions();
  }

  /**
   * Cria os dados de permissão do usuário logado.
   * Deve ser utilizado no log-in do usuário.
   */
  createPermissions() {
    // Obtém as credentials do usuário
    const credentials = this.authService.credentials;

    // Popula áreas permitidas
    this.permissionAreas = credentials?.areaPermissoes;

    this.isEditorUser = credentials?.contratante?.utilizaEditor;

    // Popula tabela de permissões
    this.permissionAreas?.forEach((area) => {
      area.permissoes.forEach((permission) => {
        this.permissionTable.push({
          code: area.id * 100 + permission.id, // Por exemplo, AreaID=1 e AcaoID=7 gera permissao code: 107
          name: `${area.nome}::${permission.acao}`, // Por exemplo, AreaNome=Dashboard e AcaoNome=Consultar, gera permissao name: 'Dashboard::Consultar'
        } as Permission);
      });
    });
  }

  /**
   * Verifica se usuário tem permissão de admin.
   * @returns Verdadeiro se usuário tem permissão de admin e falso, caso contrário.
   */
  hasAdminPermission() {
    return (
      this.permissionAreas.findIndex((ap) =>
        ap.nome.startsWith("AdminProduto")
      ) > -1
    );
  }

  /**
   * Verifica se usuário tem permissão da {@link area} recebida (por nome da área).
   * @param area Nome da área para ser verficada a permissão.
   * @returns Verdadeiro se usuário tem permissão da {@link area} e falso, caso contrário.
   */
  hasAreaPermissionOf(area: string): boolean {
    return this.permissionAreas.findIndex((ap) => ap.nome === area) > -1;
  }

  /**
   * Verifica se usuário tem permissão da {@link subArea} recebida (por nome da sub-área no modelo 'Area:SubArea').
   * @param subArea Nome da sub-área para ser verficada a permissão.
   * @returns Verdadeiro se usuário tem permissão da {@link subArea} e falso, caso contrário.
   */
  hasSubAreaPermissionOf(subArea: string): boolean {
    return (
      this.permissionAreas.findIndex((ap) => ap.nome.endsWith(subArea)) > -1
    );
  }

  /**
   * Verifica se usuário tem permissão do {@link permissionCode} recebido (código montado a partir da \<areaID\> e o \<acaoID\>) ver: {@link createPermissions}).
   * @param permissionCode Código da permissão a ser verficada. Utilizar as constantes definidas em {@link PermissionCode} ao passar esse parâmetro.
   * @returns Verdadeiro se usuário tem permissão do {@link permissionCode} e falso caso contrário.
   */
  hasPermission(permissionCode: PermissionCode): boolean {
    return (
      this.permissionTable.findIndex(
        (permission) => permission.code === permissionCode
      ) > -1
    );
  }

  hasEditorPermission(): boolean {
    return this.isEditorUser;
  }

  /**
   * Faz a limpeza das permissões existentes em memória.
   * Deve ser utilizado no log-out do usuário.
   */
  clearPermissions() {
    this.permissionAreas = [];
    this.permissionTable = [];
  }
}

/**
 * Enumerador com códigos das permissões utilizadas no front-end
 */
export enum PermissionCode {
  /**
   * Permissão AdminProduto::Consultar
   */
  PERMISSION_101 = 101,

  /**
   * Permissão AdminProduto::Alterar
   */
  PERMISSION_102 = 102,

  /**
   * Permissão AdminProduto::Remover
   */
  PERMISSION_104 = 104,

  /**
   * Permissão AdminProduto::Download
   */
  PERMISSION_107 = 107,

  /**
   * Permissão AdminProduto:ListaEmpresasContratantes::EnviarDados
   */
  PERMISSION_905 = 905, // Utilizado para enviar e-mail na lista de de contratantes

  /**
   * Permissão AdminProduto:ListaEmpresasContratantes::CadastrarEmLote
   */
  PERMISSION_906 = 906, // Cadastrar clientes em lote na lista de contratantes

  /**
   * Permissão AdminProduto:ListaUsuarios::Consultar
   */
  PERMISSION_1201 = 1201, // Visualizar usuários pelo admin do produto

  /**
   * Permissão AdminProduto:ListaUsuarios::EnviarDados
   */
  PERMISSION_1205 = 1205, // Obter link para recuperação de senha para usuário
}
